import { z } from 'zod';

/**
 * Configuration schema descriptors as sent by the bridge (GET_SCHEMA) and rendered by the web editor.
 * The Java source of truth is com.comficonf.api.schema; docs/API_CONTRACT.md describes the mapping.
 */

export const SCHEMA_FORMAT_VERSION = 1;

/** Field keys: 1-64 letters, digits, '_' or '-'. Paths join keys with '.' and index lists with '[n]'. */
export const FIELD_KEY_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/** Resource ids: namespace:name, each part 1-64 of [a-z0-9._-] starting with [a-z0-9]. */
export const RESOURCE_ID_PATTERN = /^[a-z0-9][a-z0-9._-]{0,63}:[a-z0-9][a-z0-9._-]{0,63}$/;

/** Largest integer magnitude an INTEGER field accepts (Number.MAX_SAFE_INTEGER). */
export const MAX_SAFE_INTEGER = 9_007_199_254_740_991;

export const FIELD_TYPES = [
  'string',
  'text',
  'boolean',
  'integer',
  'decimal',
  'enum',
  'stringList',
  'material',
  'object',
  'objectList',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export const SCALAR_FIELD_TYPES = [
  'string',
  'text',
  'boolean',
  'integer',
  'decimal',
  'enum',
  'material',
] as const satisfies readonly FieldType[];

export const FieldKeySchema = z.string().regex(FIELD_KEY_PATTERN);
export const ResourceIdSchema = z.string().regex(RESOURCE_ID_PATTERN);
export const FieldTypeSchema = z.enum(FIELD_TYPES);
export const ApplyModeSchema = z.enum(['live', 'restart']);
export type ApplyMode = z.infer<typeof ApplyModeSchema>;
export const MaterialFilterSchema = z.enum(['any', 'block', 'item']);
export type MaterialFilter = z.infer<typeof MaterialFilterSchema>;

export type ScalarValue = string | number | boolean;
export const ScalarValueSchema = z.union([z.string(), z.number(), z.boolean()]);

/** A JSON value inside a configuration values tree. */
export type ConfigValue = string | number | boolean | null | ConfigValue[] | ConfigObject;
export type ConfigObject = { [key: string]: ConfigValue };

export const ConfigValueSchema: z.ZodType<ConfigValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(ConfigValueSchema),
    z.record(z.string(), ConfigValueSchema),
  ]),
);
export const ConfigObjectSchema: z.ZodType<ConfigObject> = z.record(z.string(), ConfigValueSchema);

export const ChoiceSchema = z.object({
  value: z.string().min(1).max(128),
  label: z.string().min(1).max(80),
  description: z.string().max(300).optional(),
});
export type Choice = z.infer<typeof ChoiceSchema>;

export const VisibilityConditionSchema = z.object({
  /** Key of a sibling field (same parent object). */
  field: FieldKeySchema,
  operator: z.enum(['equals', 'notEquals', 'in', 'notIn']),
  /** One value for equals/notEquals, one or more for in/notIn. */
  values: z.array(ScalarValueSchema).min(1),
});
export type VisibilityCondition = z.infer<typeof VisibilityConditionSchema>;

/**
 * One field. Attributes that do not apply to the field's type are omitted.
 * `required`, `readOnly`, `sensitive`, `writeOnly` and `applyMode` are always present.
 */
export interface FieldDescriptor {
  key: string;
  type: FieldType;
  label: string;
  description?: string | undefined;
  placeholder?: string | undefined;
  required: boolean;
  readOnly: boolean;
  sensitive: boolean;
  writeOnly: boolean;
  applyMode: ApplyMode;
  default?: ConfigValue | undefined;
  visibleWhen?: VisibilityCondition | undefined;
  /** string, text; per item for stringList. */
  minLength?: number | undefined;
  maxLength?: number | undefined;
  pattern?: string | undefined;
  patternMessage?: string | undefined;
  /** integer, decimal. */
  min?: number | undefined;
  max?: number | undefined;
  step?: number | undefined;
  unit?: string | undefined;
  slider?: boolean | undefined;
  /** enum (always present), stringList (when restricted to choices). */
  choices?: Choice[] | undefined;
  /** stringList, objectList. */
  minItems?: number | undefined;
  maxItems?: number | undefined;
  ordered?: boolean | undefined;
  /** stringList. */
  uniqueItems?: boolean | undefined;
  /** material. */
  materialFilter?: MaterialFilter | undefined;
  /** object (children), objectList (item fields). */
  fields?: FieldDescriptor[] | undefined;
  /** objectList. */
  itemKey?: string | undefined;
  itemTitle?: string | undefined;
  itemLabel?: string | undefined;
}

export const FieldDescriptorSchema: z.ZodType<FieldDescriptor> = z.lazy(() =>
  z.object({
    key: FieldKeySchema,
    type: FieldTypeSchema,
    label: z.string().min(1).max(80),
    description: z.string().max(1000).optional(),
    placeholder: z.string().max(200).optional(),
    required: z.boolean(),
    readOnly: z.boolean(),
    sensitive: z.boolean(),
    writeOnly: z.boolean(),
    applyMode: ApplyModeSchema,
    default: ConfigValueSchema.optional(),
    visibleWhen: VisibilityConditionSchema.optional(),
    minLength: z.int().min(0).optional(),
    maxLength: z.int().min(1).optional(),
    pattern: z.string().max(1000).optional(),
    patternMessage: z.string().max(300).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().positive().optional(),
    unit: z.string().max(24).optional(),
    slider: z.boolean().optional(),
    choices: z.array(ChoiceSchema).max(500).optional(),
    minItems: z.int().min(0).optional(),
    maxItems: z.int().min(1).optional(),
    ordered: z.boolean().optional(),
    uniqueItems: z.boolean().optional(),
    materialFilter: MaterialFilterSchema.optional(),
    fields: z.array(FieldDescriptorSchema).optional(),
    itemKey: FieldKeySchema.optional(),
    itemTitle: FieldKeySchema.optional(),
    itemLabel: z.string().min(1).max(40).optional(),
  }),
);

export const SchemaDescriptorSchema = z.object({
  formatVersion: z.literal(SCHEMA_FORMAT_VERSION),
  fields: z.array(FieldDescriptorSchema).min(1),
});
export type SchemaDescriptor = z.infer<typeof SchemaDescriptorSchema>;

/** A sensitive field whose value was withheld from the editor. */
export const RedactedFieldSchema = z.object({
  path: z.string().min(1),
  hasValue: z.boolean(),
});
export type RedactedField = z.infer<typeof RedactedFieldSchema>;

export const SeveritySchema = z.enum(['error', 'warning']);
export type Severity = z.infer<typeof SeveritySchema>;

/** A validation error or warning. `path` is '' for resource-level issues. */
export const ValidationIssueSchema = z.object({
  path: z.string(),
  severity: SeveritySchema,
  message: z.string().min(1).max(500),
  code: z.string().regex(/^[a-z0-9_]{1,64}$/),
});
export type ValidationIssue = z.infer<typeof ValidationIssueSchema>;

/**
 * Structural issue codes produced by the bridge (and mirrored by the editor's client-side validation).
 * Plugin-defined issues use 'plugin' or their own codes.
 */
export const STRUCTURAL_ISSUE_CODES = [
  'unknown_field',
  'invalid_type',
  'required',
  'min',
  'max',
  'min_length',
  'max_length',
  'pattern',
  'invalid_choice',
  'min_items',
  'max_items',
  'duplicate_item',
  'duplicate_key',
  'unknown_material',
  'material_filter',
  'read_only',
  'not_writable',
  'no_changes',
] as const;
export type StructuralIssueCode = (typeof STRUCTURAL_ISSUE_CODES)[number];

export const ChangeKindSchema = z.enum(['modified', 'added', 'removed', 'reordered']);
export type ChangeKind = z.infer<typeof ChangeKindSchema>;

/**
 * One difference between current and proposed values, computed by the bridge.
 * `before`/`after` are present for scalar and stringList fields that are not sensitive.
 */
export const FieldChangeSchema = z.object({
  path: z.string().min(1),
  label: z.string().max(200),
  kind: ChangeKindSchema,
  applyMode: ApplyModeSchema,
  sensitive: z.boolean(),
  itemKey: z.string().optional(),
  before: ConfigValueSchema.optional(),
  after: ConfigValueSchema.optional(),
});
export type FieldChange = z.infer<typeof FieldChangeSchema>;

export const APPLY_STATUSES = [
  'APPLIED',
  'SAVED_RESTART_REQUIRED',
  'REJECTED',
  'CONFLICT',
  'FAILED',
] as const;
export const ApplyStatusSchema = z.enum(APPLY_STATUSES);
export type ApplyStatus = z.infer<typeof ApplyStatusSchema>;

/** Outcome of VALIDATE_CONFIG. */
export const ValidateOutcomeSchema = z.object({
  valid: z.boolean(),
  issues: z.array(ValidationIssueSchema),
  changes: z.array(FieldChangeSchema),
  /** Revision of the stored values when validation ran. */
  currentRevision: z.string().min(1).max(128),
  /** True when currentRevision differs from the submitted baseRevision. */
  stale: z.boolean(),
});
export type ValidateOutcome = z.infer<typeof ValidateOutcomeSchema>;

/** Outcome of APPLY_CONFIG. */
export const ApplyOutcomeSchema = z.object({
  status: ApplyStatusSchema,
  message: z.string().max(1000).optional(),
  /** APPLIED, SAVED_RESTART_REQUIRED. */
  newRevision: z.string().min(1).max(128).optional(),
  /** CONFLICT. */
  currentRevision: z.string().min(1).max(128).optional(),
  persisted: z.boolean(),
  activated: z.boolean(),
  issues: z.array(ValidationIssueSchema),
  changes: z.array(FieldChangeSchema),
  /** Paths whose changes take effect after a restart (SAVED_RESTART_REQUIRED). */
  restartRequired: z.array(z.string()),
  /** True when the bridge returned a cached result for an operationId it already executed. */
  duplicate: z.boolean(),
});
export type ApplyOutcome = z.infer<typeof ApplyOutcomeSchema>;

/** Material catalog entry from the connected server. `key` is Material.name(). */
export const MaterialEntrySchema = z.object({
  key: z.string().regex(/^[A-Z0-9_]{1,128}$/),
  label: z.string().min(1).max(128),
  category: z.string().min(1).max(64),
  block: z.boolean(),
  item: z.boolean(),
});
export type MaterialEntry = z.infer<typeof MaterialEntrySchema>;

export const PluginInfoSchema = z.object({
  /** Lowercase plugin name with characters outside [a-z0-9-] replaced by '-'. */
  slug: z.string().regex(/^[a-z0-9-]{1,64}$/),
  name: z.string().min(1).max(64),
  version: z.string().max(64),
});
export type PluginInfo = z.infer<typeof PluginInfoSchema>;

export const ResourceSummarySchema = z.object({
  id: ResourceIdSchema,
  displayName: z.string().min(1).max(80),
  description: z.string().max(500).optional(),
  plugin: PluginInfoSchema,
});
export type ResourceSummary = z.infer<typeof ResourceSummarySchema>;
